import { Component, OnDestroy, OnInit } from '@angular/core';
import { CatalogSheetService } from '@component/catalog-sheet/services/catalog-sheet.service';
import { CatalogSheetRecentUpdateDto } from '@model/catalog-sheet/catalog-sheet-recent-update-dto';
import { UpdateCatalogsheetNotificationStatusDto } from '@model/notification/update-cataloghseet-notification-status-dto';
import { UtcDateFormatPipe } from '@shared/pipe/UtcDateFormatPipe';
import { AuthMsalService } from '@shared/services/auth-msal.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzListModule } from 'ng-zorro-antd/list';
import { filter, Subject, takeUntil } from 'rxjs';
import { RouterModule } from '@angular/router';
import { refreshArrayReference } from '@shared/util/refresh.util';

@Component({
  selector: 'app-notification-update-catalog-index',
  imports: [
    TranslateModule,
    NzListModule,
    UtcDateFormatPipe,
    NzIconModule,
    NzFlexModule,
    NzCollapseModule,
    NzBadgeModule,
    RouterModule
  ],
  templateUrl: './notification-update-catalog-index.component.html',
  styleUrl: './notification-update-catalog-index.component.scss',
})
export class NotificationUpdateCatalogIndexComponent implements OnInit, OnDestroy {
  /**
   * Catalogsheets recently updated
   *
   * @public
   * @type {CatalogSheetRecentUpdateDto[]}
   */
  public catalogsheets: CatalogSheetRecentUpdateDto[] = [];

  /**
   * Open status
   *
   * @public
   * @type {boolean}
   */
  public isOpen: boolean = true;

  /**
   * Manage destruction subject
   *
   * @private
   * @type {Subject<void>}
   */
  private destroy$: Subject<void> = new Subject<void>();

  /**
   * Key used in locale storage
   *
   * @private
   * @type {!string}
   */
  private STORAGE_KEY!: string;

  /**
   * User guid
   *
   * @private
   * @type {!string}
   */
  private currentUserGuid!: string;

  constructor(
    private readonly _catalogSheetService: CatalogSheetService,
    private _authMsalService: AuthMsalService,
    private readonly _translateService: TranslateService,
  ) { }

  ngOnInit(): void {
    this._translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.catalogsheets = refreshArrayReference(this.catalogsheets);
      });

    this._authMsalService.getCurrentUser()
      .pipe(
        filter(user => !!user),
        takeUntil(this.destroy$)
      )
      .subscribe(user => {
        this.currentUserGuid = user.guid;
        this.STORAGE_KEY = `update-notification-status-${this.currentUserGuid}`;

        if (this.catalogsheets.length) {
          this.computeNotificationPanelState();
        }
      });

    this._catalogSheetService.getCatalogSheetsRecentUpdates()
      .pipe(takeUntil(this.destroy$))
      .subscribe(catalogsheets => {
        this.catalogsheets = this.mapCatalogSheetsWithEvent(catalogsheets);

        if (this.STORAGE_KEY) {
          this.computeNotificationPanelState();
        }
      });
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  /**
   * Set data in locale storage
   *
   * @public
   * @param {boolean} isOpen 
   */
  public onToggle(isOpen: boolean): void {
    this.isOpen = isOpen;

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
      isOpen,
      lastSeenAt: new Date().toISOString()
    }));
  }

  /**
    * Enriches each update row with a translation key for the event badge.
    *
    * This avoids calling a function from the template during change detection.
   *
   * @private
   * @param {CatalogSheetRecentUpdateDto[]} catalogsheets
   * @returns {CatalogSheetRecentUpdateDto[]}
   */
  private mapCatalogSheetsWithEvent(catalogsheets: CatalogSheetRecentUpdateDto[]): CatalogSheetRecentUpdateDto[] {
    return catalogsheets.map(catalogsheet => ({
      ...catalogsheet,
      eventTypeTranslationKey: catalogsheet.eventType === 'Updated'
        ? 'dashboard.notifEventUpdated'
        : 'dashboard.notifEventCreated'
    }));
  }

  /**
   * Force open status when new update
   *
   * @private
   */
  private computeNotificationPanelState(): void {
    const saved = this.loadNotificationPanelState();
    const lastUpdate = this.getLastUpdateDate(this.catalogsheets);

    if (lastUpdate && saved?.lastSeenAt && saved?.isOpen === false) {
      this.isOpen = lastUpdate.getTime() > new Date(saved.lastSeenAt).getTime();
    } else if (saved) {
      this.isOpen = saved.isOpen;
    } else {
      this.isOpen = true;
    }
  }

  /**
    * Gets the most recent valid date among eventAt/createdAt values.
   *
   * @private
   * @param {CatalogSheetRecentUpdateDto[]} catalogsheets 
   * @returns {(Date | null)} 
   */
  private getLastUpdateDate(catalogsheets: CatalogSheetRecentUpdateDto[]): Date | null {
    if (!catalogsheets.length) return null;

    const timestamps = catalogsheets
      .map(cs => {
        const rawDate = cs.eventAt ?? cs.createdAt;
        return new Date(rawDate).getTime();
      })
      .filter(timestamp => !Number.isNaN(timestamp));

    if (!timestamps.length) return null;

    return new Date(Math.max(...timestamps));
  }


  /**
   * Get notification panel status in locale storage
   *
   * @private
   * @returns {(UpdateCatalogsheetNotificationStatusDto | null)} 
   */
  private loadNotificationPanelState(): UpdateCatalogsheetNotificationStatusDto | null {
    const raw = localStorage.getItem(this.STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  }

}
